import {
  AfterViewInit,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { environment } from '../../../../environments/environment.local';
import {
  AlertService,
  AlertType,
  ButtonConfig,
  ButtonSize,
  ButtonType,
  ColumnConfig,
  ConfirmationDialogService,
  CustomAction,
  DynamicFieldDirective,
  FieldType,
  FilterConfig,
  GenericButtonComponent,
  GenericTableComponent,
  GlobalAction,
  HeaderConfigurationService,
  LoaderService,
  ModalFieldConfig,
  ModalMode,
  MoreDataConfig,
  SortMode,
  TableAction,
  TableActionConfig,
  TableSortConfig,
} from '@solcre-org/core-ui';
import { FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BookingModel } from '../../../shared/models/booking.model';
import { PortModel } from '../../../shared/models/port.model';
import { VoyageModel } from '../../../shared/models/voyage.model';
import { CurrencyModel } from '../../../shared/models/currency.model';
import { DataStoreService } from '../../../shared/services/data-store.service';
import { portUriTemplate } from '../../../shared/templates/portUriTemplate';
import { currencyUriTemplate } from '../../../shared/templates/currencyUriTemplate';
import { voyageUriTemplate } from '../../../shared/templates/voyageUriTemplate';
import { ClientModel } from '../../../shared/models/client.model';
import {
  getPaymentTermLabel,
  PAYMENT_TERMS,
} from '../../../shared/data/paymentTerms';
import { clientNameTemplate } from '../../../shared/templates/clientNameTemplate';
import { portNameTemplate } from '../../../shared/templates/portNameTemplate';
import { voyageNameTemplate } from '../../../shared/templates/voyageNameTemplate';
import { Router } from '@angular/router';
import { clientUriTemplate } from '../../../shared/templates/clientUriTemplate';
import { vesselUriTemplate } from '../../../shared/templates/vesselUriTemplate';
import { DateUtils } from '../../../shared/utils/date.utils';
import { TitleCasePipe } from '@angular/common';
import { BookingService } from '../../../shared/services/booking.service';
import { HttpErrorResponse } from '@angular/common/http';
import { BookingAttachmentsModal } from '../../components/booking-attachments-modal/booking-attachments-modal';
import { BookingImportModal } from '../../components/booking-import-modal/booking-import-modal';
import { VesselModel } from '../../../shared/models/vessel.model';
import { PortCallModel } from '../../../shared/models/port-call.model';
import { VoyageService } from '../../../shared/services/voyage.service';
import {
  createDifferentVoyageValidator,
  createPairedFieldValidator,
  extractBookingApiErrorMessage,
  LoadingVoyageState,
  normalizeBookingTransshipmentPayload,
  resolveRequiresTransshipmentToggle,
  buildPortSelectOptions,
  buildSecondVoyageSelectOptions,
  filterLegPortOptions,
  filterSharedTransshipmentPortOptions,
  filterVoyagePortOptions,
  resolveSharedConnectionPort,
  SelectOption,
  sortPortCallsBySequence,
  VoyagePortCallData,
} from './bookings-transshipment.helpers';

@Component({
  selector: 'app-bookings',
  imports: [
    DynamicFieldDirective,
    GenericButtonComponent,
    GenericTableComponent,
    TranslateModule,
    TitleCasePipe,
    BookingAttachmentsModal,
    BookingImportModal,
  ],
  templateUrl: './bookings.html',
  styleUrls: ['./bookings.css'],
})
export class Bookings implements AfterViewInit {
  //Services
  private router = inject(Router);
  private alertService: AlertService = inject(AlertService);
  private loaderService: LoaderService = inject(LoaderService);
  private bookingService: BookingService = inject(BookingService);
  private translateService: TranslateService = inject(TranslateService);
  private dataStoreService: DataStoreService = inject(DataStoreService);
  private headerConfigurationService = inject(HeaderConfigurationService);
  private confirmService: ConfirmationDialogService = inject(
    ConfirmationDialogService
  );
  private voyageService = inject(VoyageService);
  private destroyRef = inject(DestroyRef);
  private readonly portCallCacheStoreKey = 'bookings-transshipment-port-calls';
  private readonly portCallLoadingStoreKey = 'bookings-transshipment-loading';
  private readonly portsFieldVersion = signal(0);

  //Models
  bookings = signal<BookingModel[]>([]);
  readonly refreshingPorts = signal(false);
  private readonly bookingModalForm = signal<FormGroup | null>(null);

  // Port calls cache for transshipment filtering
  private primaryPortCallData = signal<VoyagePortCallData | null>(null);
  private secondaryPortCallData = signal<VoyagePortCallData | null>(null);
  private readonly loadingVoyages =
    this.dataStoreService.selectAll<LoadingVoyageState>(
      this.portCallLoadingStoreKey,
      loadingVoyage => loadingVoyage.id
    );
  private readonly allPortOptions = computed<SelectOption[]>(() =>
    buildPortSelectOptions(
      this.dataStoreService.selectAll<PortModel>('ports')()
    )
  );

  // Stable closure references — avoid recreation on each modalFields recompute
  private readonly originPortDynamicOptions = this.createLegPortOptions(
    fv => fv?.voyage,
    () => this.primaryPortCallData(),
    'primary',
    true
  );
  private readonly destinationPortDynamicOptions = this.createLegPortOptions(
    fv => fv?.secondVoyage,
    () => this.secondaryPortCallData(),
    'secondary',
    false,
    fv => fv?.voyage,
    () => this.primaryPortCallData()
  );
  private readonly transshipmentPortDynamicOptions =
    this.createTransshipmentPortOptions();
  private readonly secondVoyageDynamicOptions = this.createSecondVoyageOptions();
  private readonly resetPortsForVoyageChange = (_: any, fv: BookingModel) => ({
    ...fv,
    originPort: null,
    destinationPort: null,
    transshipmentPort: null,
  });
  private readonly resetDestinationForSecondVoyageChange = (
    selection: any,
    fv: BookingModel
  ) => {
    const newSecondVoyageId = selection?.value;
    const newFv = { ...fv, secondVoyage: newSecondVoyageId };
    const currentHub = fv.transshipmentPort;

    if (newSecondVoyageId && newSecondVoyageId !== 'TBN' && currentHub) {
      const cached = this.getCachedPortCallData(newSecondVoyageId);
      if (cached) {
        const isValid = cached.portCalls.some((pc: PortCallModel) => pc.port === currentHub);
        if (!isValid) {
          this.alertService.show({ type: AlertType.DEFAULT, message: this.translateService.instant('bookings.transshipmentPortInvalidWarning') });
          newFv.transshipmentPort = null;
        }
      } else {
        newFv.transshipmentPort = null;
        this.syncBookingField(
          'transshipmentPort',
          newFv as BookingModel,
          null
        );
        this.loadPortCalls(newSecondVoyageId, 'secondary');
      }
    }

    this.tryAutoSelectConnectionPort(newFv);

    return newFv;
  };

  private readonly autoSelectConnectionPortOnDestinationChange = (
    _selection: any,
    fv: BookingModel
  ) => {
    this.tryAutoSelectConnectionPort(fv);
    return fv;
  };

  activeFilters = signal<Map<string, any>>(new Map());
  activeBookingForUploadAttachments = signal<BookingModel | null>(null);
  showImportModal = signal<boolean>(false);

  //Others
  @ViewChild(GenericTableComponent) table!: GenericTableComponent<BookingModel>;
  @ViewChild('statusTemplate', { static: true }) statusTemplate!: TemplateRef<{
    $implicit: BookingModel;
    column: ColumnConfig<BookingModel>;
  }>;
  @ViewChild('voyageTemplate', { static: true }) voyageTemplate!: TemplateRef<{
    $implicit: BookingModel;
    column: ColumnConfig<BookingModel>;
  }>;
  @ViewChild('clientTemplate', { static: true }) clientTemplate!: TemplateRef<{
    $implicit: BookingModel;
    column: ColumnConfig<BookingModel>;
  }>;
  @ViewChild('originPortFieldTemplate', { static: true })
  originPortFieldTemplate!: TemplateRef<any>;
  @ViewChild('destinationPortFieldTemplate', { static: true })
  destinationPortFieldTemplate!: TemplateRef<any>;
  @ViewChild('transshipmentPortFieldTemplate', { static: true })
  transshipmentPortFieldTemplate!: TemplateRef<any>;

  //Configs
  endpoint = environment.API.BOOKING_URI;
  columns: ColumnConfig<BookingModel>[] = [];
  modalFields = computed<ModalFieldConfig<BookingModel>[]>(
    () => {
      const loadingVoyages = this.loadingVoyages();
      this.portsFieldVersion();

      return [
        {
          key: 'client',
          label: 'bookings.client',
          placeholder: this.translateService.instant(
            'bookings.clientPlaceholder'
          ),
          type: FieldType.SELECT,
          searchable: true,
          readonly: false,
          defaultValue: '',
          options: this.dataStoreService
            .selectAll<ClientModel>('clients')()
            .map((client: ClientModel) => ({
              value: client.id,
              label: clientNameTemplate(client),
            })),
          validators: [Validators.required],
          errorMessages: {
            required: this.translateService.instant('validators.required'),
          },
          customViewTemplate: (clientId: string) => this.clientName(clientId),
        },
        {
          key: 'voyage',
          label: 'bookings.firstVoyage',
          placeholder: this.translateService.instant(
            'bookings.firstVoyagePlaceholder'
          ),
          type: FieldType.SELECT,
          searchable: true,
          readonly: false,
          defaultValue: '',
          options: this.dataStoreService
            .selectAll<VoyageModel>('voyages')()
            .map((voyage: VoyageModel) => ({
              value: voyage.id,
              label: this.voyageSelectLabel(voyage),
            })),
          validators: [Validators.required],
          errorMessages: {
            required: this.translateService.instant('validators.required'),
          },
          onSelectionChange: this.resetPortsForVoyageChange,
          customViewTemplate: (voyageDetails: VoyageModel) => {
            return this.voyageName(voyageDetails);
          },
        },
        {
          key: 'originPort',
          label: 'bookings.originPort',
          placeholder: this.translateService.instant(
            'bookings.originPortPlaceholder'
          ),
          type: FieldType.SELECT,
          searchable: true,
          readonly: false,
          defaultValue: '',
          disabled: (fv: BookingModel) =>
            this.isPortCallsLoading(fv?.voyage, loadingVoyages),
          dynamicOptions: this.originPortDynamicOptions,
          validators: [Validators.required],
          errorMessages: {
            required: this.translateService.instant('validators.required'),
          },
          modes: {
            [ModalMode.CREATE]: {
              customViewTemplate: this.originPortFieldTemplate,
            },
            [ModalMode.EDIT]: {
              customViewTemplate: this.originPortFieldTemplate,
            },
          },
          customViewTemplate: (portId: string) => {
            return this.portName(portId);
          },
        },
        {
          key: 'requiresTransshipmentToggle',
          label: 'bookings.requiresTransshipment',
          placeholder: this.translateService.instant('bookings.requiresTransshipmentPlaceholder'),
          type: FieldType.SELECT,
          searchable: false,
          readonly: false,
          defaultValue: false,
          options: [
            { value: true, label: this.translateService.instant('common.yes') },
            { value: false, label: this.translateService.instant('common.no') }
          ],
          modes: {
            [ModalMode.EDIT]: {
              dynamicValue: (formValue: BookingModel) =>
                resolveRequiresTransshipmentToggle(formValue),
            },
          },
          customViewTemplate: (value: boolean) =>
            value
              ? this.translateService.instant('common.yes')
              : this.translateService.instant('common.no'),
        },
        ...this.buildTransshipmentFields(loadingVoyages),
        {
          key: 'destinationPort',
          label: 'bookings.destinationPort',
          placeholder: this.translateService.instant(
            'bookings.destinationPortPlaceholder'
          ),
          type: FieldType.SELECT,
          searchable: true,
          readonly: false,
          defaultValue: '',
          disabled: (fv: BookingModel) =>
            this.isDestinationPortLoading(fv, loadingVoyages),
          dynamicOptions: this.destinationPortDynamicOptions,
          onSelectionChange: this.autoSelectConnectionPortOnDestinationChange,
          validators: [Validators.required],
          errorMessages: {
            required: this.translateService.instant('validators.required'),
          },
          modes: {
            [ModalMode.CREATE]: {
              customViewTemplate: this.destinationPortFieldTemplate,
            },
            [ModalMode.EDIT]: {
              customViewTemplate: this.destinationPortFieldTemplate,
            },
          },
          customViewTemplate: (portId: string) => {
            return this.portName(portId);
          },
        },
        {
          key: 'shipper',
          label: 'bookings.shipper',
          placeholder: this.translateService.instant(
            'bookings.shipperPlaceholder'
          ),
          type: FieldType.TEXT,
          readonly: false,
          validators: [Validators.required],
          errorMessages: {
            required: this.translateService.instant('validators.required'),
          },
        },
        {
          key: 'consignee',
          label: 'bookings.consignee',
          placeholder: this.translateService.instant(
            'bookings.consigneePlaceholder'
          ),
          type: FieldType.TEXT,
          readonly: false,
        },
        {
          key: 'requestDate',
          label: 'bookings.requestDate',
          type: FieldType.DATE,
          readonly: false,
          validators: [Validators.required],
          errorMessages: {
            required: this.translateService.instant('validators.required'),
          },
        },
        {
          key: 'billOfLading',
          label: 'bookings.billOfLading',
          placeholder: this.translateService.instant(
            'bookings.billOfLadingPlaceholder'
          ),
          type: FieldType.TEXT,
          readonly: false,
          defaultValue: '',
        },
        {
          key: 'currency',
          label: 'bookings.currency',
          placeholder: this.translateService.instant(
            'bookings.currencyPlaceholder'
          ),
          type: FieldType.SELECT,
          searchable: true,
          readonly: false,
          options: this.dataStoreService
            .selectAll<CurrencyModel>('currencies')()
            .map((currency: CurrencyModel) => ({
              value: currency.id,
              label: currency.getReference(),
            })),
          validators: [Validators.required],
          errorMessages: {
            required: this.translateService.instant('validators.required'),
          },
          customViewTemplate: (currencyId: string) => {
            return this.currencyName(currencyId);
          },
        },
        {
          key: 'paymentTerms',
          label: 'bookings.paymentTerms',
          placeholder: this.translateService.instant(
            'bookings.paymentTermsPlaceholder'
          ),
          type: FieldType.SELECT,
          searchable: true,
          readonly: false,
          options: PAYMENT_TERMS,
          validators: [Validators.required],
          errorMessages: {
            required: this.translateService.instant('validators.required'),
          },
          customViewTemplate: (paymentTermId: string) => {
            return getPaymentTermLabel(paymentTermId);
          },
        },
        {
          key: 'totalCubicMeters',
          label: 'bookings.totalCubicMeters',
          type: FieldType.NUMBER,
          readonly: true,
          visible: false,
          modes: {
            [ModalMode.VIEW]: { visible: true },
          },
        },
        {
          key: 'totalFreight',
          label: 'bookings.totalFreight',
          type: FieldType.NUMBER,
          readonly: true,
          visible: false,
          modes: {
            [ModalMode.VIEW]: { visible: true },
          },
        },
        {
          key: 'totalLinearMeters',
          label: 'bookings.totalLinearMeters',
          type: FieldType.NUMBER,
          readonly: true,
          visible: false,
          modes: {
            [ModalMode.VIEW]: { visible: true },
          },
        },
        {
          key: 'totalSquareMeters',
          label: 'bookings.totalSquareMeters',
          type: FieldType.NUMBER,
          readonly: true,
          visible: false,
          modes: {
            [ModalMode.VIEW]: { visible: true },
          },
        },
        {
          key: 'totalWeight',
          label: 'bookings.totalWeight',
          type: FieldType.NUMBER,
          readonly: true,
          visible: false,
          modes: {
            [ModalMode.VIEW]: { visible: true },
          },
        },
      ] as any as ModalFieldConfig<BookingModel>[];
    }
  );

  private buildTransshipmentFields(
    loadingVoyages: readonly LoadingVoyageState[]
  ): ModalFieldConfig<BookingModel>[] {
    return [
      {
        key: 'secondVoyage',
        label: 'bookings.secondVoyage',
        placeholder: this.translateService.instant('bookings.secondVoyagePlaceholder'),
        type: FieldType.SELECT,
        searchable: true,
        readonly: false,
        defaultValue: '',
        visible: (formValue: BookingModel) =>
          this.shouldShowTransshipmentFields(formValue),
        dynamicOptions: this.secondVoyageDynamicOptions,
        onSelectionChange: this.resetDestinationForSecondVoyageChange,
        validators: (formValue: BookingModel) =>
          this.buildSecondVoyageValidators(formValue),
        errorMessages: {
          required: this.translateService.instant(
            'bookings.secondVoyageRequired'
          ),
          pairedFieldRequired: this.translateService.instant(
            'bookings.secondVoyagePairRequired'
          ),
          sameVoyage: this.translateService.instant(
            'bookings.secondVoyageDifferent'
          ),
        },
        customViewTemplate: (voyageDetails: VoyageModel) => {
          return this.voyageName(voyageDetails);
        },
      },
      {
        key: 'transshipmentPort',
        label: 'bookings.transshipmentPort',
        placeholder: this.translateService.instant(
          'bookings.transshipmentPortPlaceholder'
        ),
        type: FieldType.SELECT,
        searchable: true,
        readonly: false,
        defaultValue: '',
        visible: (formValue: BookingModel) =>
          this.shouldShowTransshipmentFields(formValue),
        disabled: (formValue: BookingModel) =>
          this.isTransshipmentLoading(formValue, loadingVoyages),
        dynamicOptions: this.transshipmentPortDynamicOptions,
        validators: (formValue: BookingModel) =>
          this.buildTransshipmentPortValidators(formValue),
        errorMessages: {
          required: this.translateService.instant(
            'bookings.transshipmentPortRequired'
          ),
          pairedFieldRequired: this.translateService.instant(
            'bookings.transshipmentPortPairRequired'
          ),
        },
        modes: {
          [ModalMode.CREATE]: {
            customViewTemplate: this.transshipmentPortFieldTemplate,
          },
          [ModalMode.EDIT]: {
            customViewTemplate: this.transshipmentPortFieldTemplate,
          },
        },
        customViewTemplate: (portId: string) => {
          return this.portName(portId);
        },
      },
    ] as any as ModalFieldConfig<BookingModel>[];
  }

  refreshPortsButtonConfig = computed<ButtonConfig>(() => ({
    type: ButtonType.ICON,
    size: ButtonSize.SMALL,
    icon: 'icon-reload',
    ariaLabel: this.translateService.instant('bookings.refreshPorts'),
    tooltip: this.translateService.instant('bookings.refreshPorts'),
    loading: this.refreshingPorts(),
    disabled: this.refreshingPorts(),
  }));

  private shouldShowTransshipmentFields(
    formValue?: BookingModel | null
  ): boolean {
    return resolveRequiresTransshipmentToggle(formValue);
  }

  private buildSecondVoyageValidators(
    formValue: BookingModel
  ): ValidatorFn[] {
    const validators: ValidatorFn[] = [
      createPairedFieldValidator(
        formValue?.transshipmentPort,
        'pairedFieldRequired'
      ),
      createDifferentVoyageValidator(formValue?.voyage),
    ];

    if (this.shouldShowTransshipmentFields(formValue)) {
      validators.unshift(Validators.required);
    }

    return validators;
  }

  private buildTransshipmentPortValidators(
    formValue: BookingModel
  ): ValidatorFn[] {
    const validators: ValidatorFn[] = [
      createPairedFieldValidator(
        formValue?.secondVoyage,
        'pairedFieldRequired'
      ),
    ];

    if (this.shouldShowTransshipmentFields(formValue)) {
      validators.unshift(Validators.required);
    }

    return validators;
  }

  private isDestinationPortLoading(
    formValue: BookingModel,
    loadingVoyages: readonly LoadingVoyageState[]
  ): boolean {
    const activeVoyageUri = this.shouldShowTransshipmentFields(formValue)
      ? formValue?.secondVoyage
      : formValue?.voyage;

    return this.isPortCallsLoading(activeVoyageUri, loadingVoyages);
  }

  private isTransshipmentLoading(
    formValue: BookingModel,
    loadingVoyages: readonly LoadingVoyageState[]
  ): boolean {
    return (
      this.isPortCallsLoading(formValue?.voyage, loadingVoyages) ||
      this.isPortCallsLoading(formValue?.secondVoyage, loadingVoyages)
    );
  }

  globalActions: GlobalAction<BookingModel>[] = [
    {
      label: this.translateService.instant('bookings.import.action'),
      icon: 'fa-regular fa-file-import',
      callback: () => {
        this.openImportModal();
      },
    },
    {
      label: this.translateService.instant('bookings.export'),
      icon: 'fa-regular fa-file-excel',
      callback: () => {
        this.exportAllBookings();
      },
    },
  ];
  actions: TableActionConfig[] = [
    { action: TableAction.EDIT, isExtra: false },
    { action: TableAction.DELETE, isExtra: true },
    { action: TableAction.CREATE, isExtra: false },
  ];
  moreData: MoreDataConfig<any>[] = [
    {
      key: 'ports',
      endpoint: environment.API.PORTS_URI + '?pagination=false&order[fullName]=asc',
      model: PortModel,
    },
    {
      key: 'voyages',
      endpoint: environment.API.VOYAGES_URI + '?pagination=false',
      model: VoyageModel,
    },
    {
      key: 'currencies',
      endpoint: environment.API.CURRENCY_URI,
      model: CurrencyModel,
    },
    {
      key: 'clients',
      endpoint: environment.API.CLIENTS_URI + '?pagination=false&order[commercialName]=asc',
      model: ClientModel,
    },
    {
      key: 'vessels',
      endpoint: environment.API.VESSELS_URI + '?pagination=false',
      model: VesselModel,
    },
  ];
  customActions: CustomAction<BookingModel>[] = [
    {
      buttonConfig: {
        type: ButtonType.ICON,
        icon: 'fa-regular fa-file-arrow-up',
      },
      isExtra: false,
      tooltipPosition: 'top',
      title: this.translateService.instant('bookings.attachmentsModal'),
      callback: (row: BookingModel) => {
        this.activeBookingForUploadAttachments.set(row);
      },
    },
    {
      buttonConfig: {
        type: ButtonType.ICON,
        icon: 'icon-arrow-right',
      },
      isExtra: false,
      tooltipPosition: 'top',
      title: this.translateService.instant('bookings.itemsAction'),
      callback: (row: BookingModel) => {
        this.router.navigate(['/app/bookings', row.id], {
          queryParams: { bookingCode: row.bookingCode },
        });
      },
    },
  ];
  customFilters = computed<FilterConfig<BookingModel>[]>(() => [
    {
      key: 'bookingCode',
      label: 'bookings.bookingNumber',
      placeholder: 'bookings.bookingNumberPlaceholder',
      type: FieldType.TEXT,
      readonly: false,
      filterFn: (row: BookingModel, value: any) => value,
    },
    {
      key: 'client.commercialName',
      label: 'bookings.comercialClientName',
      placeholder: 'bookings.comercialClientNamePlaceholder',
      type: FieldType.TEXT,
      readonly: false,
      filterFn: (row: BookingModel, value: any) => value,
    },
    {
      key: 'shipper',
      label: 'bookings.shipper',
      placeholder: 'bookings.shipperPlaceholder',
      type: FieldType.TEXT,
      readonly: false,
      filterFn: (row: BookingModel, value: any) => value,
    },
    {
      key: 'consignee',
      label: 'bookings.consignee',
      placeholder: 'bookings.consigneePlaceholder',
      type: FieldType.TEXT,
      readonly: false,
      filterFn: (row: BookingModel, value: any) => value,
    },
    {
      key: 'originPort.portCode',
      label: 'bookings.originPort',
      placeholder: 'bookings.originPortPlaceholder',
      type: FieldType.SELECT,
      options: this.dataStoreService
        .selectAll<PortModel>('ports')()
        .map((port: PortModel) => ({
          value: port.portCode,
          label: portNameTemplate(port),
        })),
      customViewTemplate: (portId: string) => {
        return this.portName(portId);
      },
      readonly: false,
      filterFn: (row: BookingModel, value: any) => value,
    },
    {
      key: 'destinationPort.portCode',
      label: 'bookings.destinationPort',
      placeholder: 'bookings.destinationPortPlaceholder',
      type: FieldType.SELECT,
      options: this.dataStoreService
        .selectAll<PortModel>('ports')()
        .map((port: PortModel) => ({
          value: port.portCode,
          label: portNameTemplate(port),
        })),
      customViewTemplate: (portId: string) => {
        return this.portName(portId);
      },
      readonly: false,
      filterFn: (row: BookingModel, value: any) => value,
    },
    {
      key: 'voyage',
      label: 'bookings.voyage',
      placeholder: 'bookings.voyagePlaceholder',
      type: FieldType.SELECT,
      options: this.dataStoreService
        .selectAll<VoyageModel>('voyages')()
        .map((voyage: VoyageModel) => ({
          value: voyage.id,
          label: voyage.getReference() ?? ''
        })),
      readonly: false,
      filterFn: (row: BookingModel, value: any) => value
    },
    {
      key: 'voyage.vessel',
      label: 'bookings.vesselName',
      placeholder: 'bookings.vesselNamePlaceholder',
      type: FieldType.SELECT,
      options: this.dataStoreService
        .selectAll<VesselModel>('vessels')()
        .map((vessel: VesselModel) => ({
          value: vessel.id,
          label: vessel.vesselName ?? '',
        })),
      readonly: false,
      filterFn: (row: BookingModel, value: any) => value
    },
    {
      key: 'secondVoyage',
      label: 'bookings.secondVoyage',
      placeholder: 'bookings.secondVoyagePlaceholder',
      type: FieldType.SELECT,
      options: this.dataStoreService
        .selectAll<VoyageModel>('voyages')()
        .map((voyage: VoyageModel) => ({
          value: voyage.id,
          label: this.voyageSelectLabel(voyage),
        })),
      readonly: false,
      filterFn: (row: BookingModel, value: any) => value,
    },
    {
      key: 'transshipmentPort.portCode',
      label: 'bookings.transshipmentPort',
      placeholder: 'bookings.transshipmentPortPlaceholder',
      type: FieldType.SELECT,
      options: this.dataStoreService
        .selectAll<PortModel>('ports')()
        .map((port: PortModel) => ({
          value: port.portCode,
          label: portNameTemplate(port),
        })),
      readonly: false,
      filterFn: (row: BookingModel, value: any) => value,
    },
    {
      key: 'isTbn',
      label: 'bookings.isTBN',
      placeholder: 'bookings.isTBNPlaceholder',
      type: FieldType.SELECT,
      options: [
        { value: true, label: this.translateService.instant('common.yes') },
        { value: false, label: this.translateService.instant('common.no') }
      ],
      readonly: false,
      filterFn: (row: BookingModel, value: any) => value,
    },
    {
      key: 'requestDate[after]',
      label: 'bookings.requestDateStart',
      type: FieldType.DATE,
      readonly: false,
      filterFn: (row: BookingModel, value: any) => value,
    },
    {
      key: 'requestDate[before]',
      label: 'bookings.requestDateEnd',
      type: FieldType.DATE,
      readonly: false,
      filterFn: (row: BookingModel, value: any) => value,
    },
  ]);

  bookingModelFactory = (data: Partial<BookingModel>): BookingModel => {
    return new BookingModel().fromJSON(data);
  };

  sortConfig = signal<TableSortConfig>({
    mode: SortMode.SERVER,
    multiColumn: false,
    serverSortParam: 'order'
  });

  //Components events
  ngOnInit() {
    this.clearLoadingVoyages();
    this.destroyRef.onDestroy(() => this.clearLoadingVoyages());

    this.headerConfigurationService.setEndpointHeaders(this.endpoint, {
      patch: [{ 'Content-Type': 'application/merge-patch+json' }],
    });
  }

  ngAfterViewInit(): void {
    this.columns = [
      {
        label: 'bookings.bookingCode',
        key: 'bookingCode',
        sortable: true,
        sortKey: 'bookingCode',
      },
      {
        label: 'bookings.billOfLading',
        key: 'billOfLading',
        sortable: true,
        sortKey: 'billOfLading',
      },
      {
        label: 'bookings.client',
        key: 'client',
        sortable: true,
        sortKey: 'client.commercialName',
        template: this.clientTemplate,
      },
      {
        label: 'bookings.voyage',
        key: 'voyageDetails',
        sortable: true,
        sortKey: 'voyage.id',
        template: this.voyageTemplate,
      },
      {
        label: 'bookings.vesselName',
        key: 'voyageDetails',
        sortable: true,
        sortKey: 'voyage.vessel.vesselName',
        format: (voyageDetails: VoyageModel) => this.vesselNameFromVoyage(voyageDetails),
      },
      {
        label: 'bookings.connectionType',
        key: 'connectionTypeLabelKey',
        sortable: true,
        sortKey: 'secondVoyage',
        format: (connectionTypeLabelKey: string) =>
          this.translateService.instant(connectionTypeLabelKey),
      },
      {
        label: 'bookings.originPort',
        key: 'originPort',
        sortable: true,
        sortKey: 'originPort.fullName',
        format: (portId: string) => {
          return this.portName(portId);
        },
      },
      {
        label: 'bookings.destinationPort',
        key: 'destinationPort',
        sortable: true,
        sortKey: 'destinationPort.fullName',
        format: (portId: string) => {
          return this.portName(portId);
        },
      },
      {
        label: 'bookings.requestDate',
        key: 'requestDate',
        sortable: true,
        sortKey: 'requestDate',
        format: (value: any) => DateUtils.formatDateOnlyUTC(value),
      },
      {
        label: 'bookings.status',
        key: 'status',
        sortable: true,
        sortKey: 'status',
        template: this.statusTemplate,
      },
      {
        label: 'bookings.revenue',
        key: 'revenue',
        sortable: true,
        sortKey: 'revenue',
        format: (revenue: number) => `$ ${Number(revenue ?? 0).toFixed(2)}`
      },
    ];
  }

  onMoreDataLoaded(event: Map<string, any[]>): void {
    if (!event) {
      return;
    }

    const portsData = event.get('ports');
    if (Array.isArray(portsData)) {
      this.hydratePorts(portsData);
    }

    const voyagesData = event.get('voyages');
    if (Array.isArray(voyagesData)) {
      this.dataStoreService.hydrate<VoyageModel>(
        'voyages',
        voyagesData.map((voyage: VoyageModel) => {
          voyage.id = voyageUriTemplate(voyage.id);
          return voyage;
        })
      );
    }

    const currienciesData = event.get('currencies');
    if (Array.isArray(currienciesData)) {
      this.dataStoreService.hydrate<CurrencyModel>(
        'currencies',
        currienciesData.map((currency: CurrencyModel) => {
          currency.id = currencyUriTemplate(currency.id);
          return currency;
        })
      );
    }

    const clientsData = event.get('clients');
    if (Array.isArray(clientsData)) {
      this.dataStoreService.hydrate<ClientModel>(
        'clients',
        clientsData.map((client: ClientModel) => {
          client.id = clientUriTemplate(client.id);
          return client;
        })
      );
    }

    const vesselsData = event.get('vessels');
    if (Array.isArray(vesselsData)) {
      this.dataStoreService.hydrate<VesselModel>(
        'vessels',
        vesselsData.map((vessel: VesselModel) => {
          vessel.id = vesselUriTemplate(vessel.id);
          return vessel;
        })
      );
    }
  }

  onFilterChange(filters?: Map<string, any>) {
    this.activeFilters.set(filters ?? new Map());
  }

  getBookingFieldLabel(
    fieldKey: 'originPort' | 'destinationPort' | 'transshipmentPort',
    booking: BookingModel | null | undefined
  ): string {
    if (fieldKey === 'destinationPort' && this.shouldShowTransshipmentFields(booking)) {
      return 'bookings.finalDestination';
    }

    return this.getBookingFieldConfig(fieldKey).label;
  }

  bookingFieldIsRequired(
    fieldKey: 'originPort' | 'destinationPort' | 'transshipmentPort',
    booking: BookingModel | null | undefined
  ): boolean {
    const field = this.getBookingFieldConfig(fieldKey);
    const validatorsConfig = field.validators;
    const validators =
      typeof validatorsConfig === 'function'
        ? validatorsConfig(booking as BookingModel)
        : validatorsConfig ?? [];

    return validators.includes(Validators.required);
  }

  onBookingFormReady(form: FormGroup): void {
    this.bookingModalForm.set(form);
  }

  private getBookingFieldConfig(
    fieldKey: 'originPort' | 'destinationPort' | 'transshipmentPort'
  ): ModalFieldConfig<BookingModel> {
    const field = this.modalFields().find(
      modalField => modalField.key === fieldKey
    );

    if (!field) {
      throw new Error(`Booking field config not found for ${fieldKey}`);
    }

    return field;
  }

  getBookingPortInputField(
    fieldKey: 'originPort' | 'destinationPort' | 'transshipmentPort',
    mode: ModalMode
  ): ModalFieldConfig<BookingModel> {
    const field = this.getBookingFieldConfig(fieldKey);
    const modeConfig = field.modes?.[mode];

    return {
      ...field,
      label: '',
      modes: modeConfig
        ? {
            ...field.modes,
            [mode]: {
              ...modeConfig,
              customViewTemplate: undefined,
            },
          }
        : field.modes,
    };
  }

  resolveBookingFieldErrors(
    fieldKey: 'originPort' | 'destinationPort' | 'transshipmentPort',
    contextErrors: string[] = []
  ): string[] {
    if (contextErrors.length > 0) {
      return contextErrors;
    }

    const control = this.bookingModalForm()?.get(fieldKey);
    if (!control || !control.errors || (!control.touched && !control.dirty)) {
      return [];
    }

    const field = this.getBookingFieldConfig(fieldKey);
    return Object.keys(control.errors).map(
      errorKey =>
        field.errorMessages?.[errorKey] ??
        this.translateService.instant('errors.generic')
    );
  }

  syncBookingField(
    fieldKey: 'originPort' | 'destinationPort' | 'transshipmentPort',
    booking: BookingModel | null | undefined,
    value: any,
    touch = false
  ): void {
    if (!booking) {
      return;
    }

    booking[fieldKey] = value;

    const control = this.bookingModalForm()?.get(fieldKey);
    if (!control) {
      return;
    }

    control.setValue(value, { emitEvent: false });
    control.markAsDirty();

    if (touch) {
      control.markAsTouched();
    }

    control.updateValueAndValidity({ emitEvent: false });
  }

  markBookingFieldTouched(
    fieldKey: 'originPort' | 'destinationPort' | 'transshipmentPort'
  ): void {
    const control = this.bookingModalForm()?.get(fieldKey);
    if (!control) {
      return;
    }

    control.markAsTouched();
    control.updateValueAndValidity({ emitEvent: false });
  }

  refreshPorts(): void {
    if (this.refreshingPorts()) {
      return;
    }

    this.refreshingPorts.set(true);
    this.voyageService
      .fetchPorts()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ports => {
          this.hydratePorts(ports);
          this.refreshingPorts.set(false);
          this.alertService.show({
            type: AlertType.SUCCESS,
            message: 'bookings.refreshPortsSuccess',
            duration: 2000,
            inBottom: true,
          });
        },
        error: (error: HttpErrorResponse) => {
          this.refreshingPorts.set(false);
          this.alertService.show({
            type: AlertType.ERROR,
            message: error.error?.detail ?? 'errors.generic',
            duration: 2000,
            inBottom: true,
          });
        },
      });
  }

  onCloseBookingAttachments(): void {
    this.activeBookingForUploadAttachments.set(null);
  }

  /**
   * Returns a memoized `dynamicOptions` function for the second-voyage dropdown.
   *
   * Excludes the primary voyage from the list so the user cannot select the same
   * voyage for both legs. The result is cached by `(primaryVoyage, voyages.length)`
   * to avoid unnecessary re-computation on every form value change.
   */
  private createSecondVoyageOptions(): (
    formValue: BookingModel
  ) => SelectOption[] {
    let lastKey = '';
    let cached: SelectOption[] = [];

    return (formValue: BookingModel) => {
      const primaryVoyage = formValue?.voyage;
      const allVoyages = this.dataStoreService.selectAll<VoyageModel>('voyages')();
      const key = `${primaryVoyage}|${allVoyages.length}`;

      if (key === lastKey) {
        return cached;
      }

      lastKey = key;
      cached = buildSecondVoyageSelectOptions(
        allVoyages,
        primaryVoyage,
        (voyage: VoyageModel) => this.voyageSelectLabel(voyage)
      );
      return cached;
    };
  }

  /**
   * Returns a memoized `dynamicOptions` function that filters port options
   * for a booking leg (origin or destination).
   *
   * When transshipment is active, ports are restricted to those belonging to the
   * leg's voyage and positioned before or after the transshipment hub depending
   * on `beforeHub`. Without transshipment, ports are filtered to those of the
   * filter voyage (if provided) or shown unfiltered.
   *
   * Port calls are loaded on demand and cached in `DataStoreService` to avoid
   * redundant API calls. The returned closure is stable (created once per field)
   * and uses key-based memoization to skip recomputation when inputs haven't changed.
   *
   * @param getVoyageUri - Extracts the voyage URI for this leg from the form value.
   * @param getPortCallData - Returns the cached port-call data signal for this leg.
   * @param target - Which port-call data signal to populate (`'primary'` or `'secondary'`).
   * @param beforeHub - `true` for origin (ports before the hub); `false` for destination.
   * @param getFilterVoyageUri - Optional: extracts the voyage URI used to filter when no transshipment.
   * @param getFilterPortCallData - Optional: returns the port-call data used for filter voyage.
   */
  private createLegPortOptions(
    getVoyageUri: (fv: BookingModel) => string | null | undefined,
    getPortCallData: () => VoyagePortCallData | null,
    target: 'primary' | 'secondary',
    beforeHub: boolean,
    getFilterVoyageUri?: (fv: BookingModel) => string | null | undefined,
    getFilterPortCallData?: () => VoyagePortCallData | null
  ): (formValue: BookingModel) => SelectOption[] {
    let lastKey = '';
    let cached: SelectOption[] = [];
    const filterTarget: 'primary' | 'secondary' =
      getFilterPortCallData != null ? 'primary' : target;

    return (formValue: BookingModel) => {
      const voyageUri = getVoyageUri(formValue);
      const hub = formValue?.transshipmentPort;
      const portCallData = getPortCallData();
      const requiresTransshipment =
        resolveRequiresTransshipmentToggle(formValue);
      const filterVoyageUri = (getFilterVoyageUri ?? getVoyageUri)(formValue);
      const filterPortCallData = (getFilterPortCallData ?? getPortCallData)();
      const portOptionsVersion = this.portsFieldVersion();
      const key = [
        voyageUri,
        hub,
        portCallData?.voyageUri,
        requiresTransshipment,
        filterVoyageUri,
        filterPortCallData?.voyageUri,
        this.allPortOptions().length,
        portOptionsVersion,
      ].join('|');

      if (key === lastKey) {
        return cached;
      }

      lastKey = key;

      if (!requiresTransshipment) {
        cached = this.getVoyagePortOptions(
          filterVoyageUri,
          filterPortCallData,
          filterTarget
        );
        return cached;
      }

      if (voyageUri === 'TBN') {
        const primaryData = this.primaryPortCallData();
        this.ensurePortCallsLoaded(filterVoyageUri, primaryData, 'primary');
        cached = this.allPortOptions();
        return cached;
      }

      cached = this.getLegPortOptions(
        voyageUri,
        portCallData,
        target,
        hub,
        beforeHub
      );
      return cached;
    };
  }

  /**
   * Returns a memoized `dynamicOptions` function for the transshipment-port dropdown.
   *
   * The available ports are the intersection of port calls shared between the
   * primary and secondary voyages — i.e., only ports that appear on both routes
   * are valid transshipment hubs. Falls back to all ports while either voyage's
   * port calls are still loading.
   */
  private createTransshipmentPortOptions(): (
    formValue: BookingModel
  ) => SelectOption[] {
    let lastKey = '';
    let cached: SelectOption[] = [];

    return (formValue: BookingModel) => {
      const primaryVoyageUri = formValue?.voyage;
      const secondaryVoyageUri = formValue?.secondVoyage;
      const primaryData = this.primaryPortCallData();
      const secondaryData = this.secondaryPortCallData();
      const portOptionsVersion = this.portsFieldVersion();
      const key = [
        primaryVoyageUri,
        secondaryVoyageUri,
        primaryData?.voyageUri,
        secondaryData?.voyageUri,
        this.allPortOptions().length,
        portOptionsVersion,
      ].join('|');

      if (key === lastKey) {
        return cached;
      }

      lastKey = key;

      const allPorts = this.allPortOptions();

      if (secondaryVoyageUri === 'TBN') {
        const primaryData = this.primaryPortCallData();
        this.ensurePortCallsLoaded(primaryVoyageUri, primaryData, 'primary');
        cached = allPorts;
        return cached;
      }

      this.ensurePortCallsLoaded(primaryVoyageUri, primaryData, 'primary');
      this.ensurePortCallsLoaded(
        secondaryVoyageUri,
        secondaryData,
        'secondary'
      );

      if (
        !this.hasPortCallsForVoyage(primaryVoyageUri, primaryData) ||
        !this.hasPortCallsForVoyage(secondaryVoyageUri, secondaryData)
      ) {
        cached = allPorts;
        return cached;
      }

      cached = filterSharedTransshipmentPortOptions(
        allPorts,
        primaryData.portCalls,
        secondaryData.portCalls
      );
      return cached;
    };
  }

  private ensurePortCallsLoaded(
    voyageUri: string | null | undefined,
    portCallData: VoyagePortCallData | null,
    target: 'primary' | 'secondary'
  ): void {
    if (voyageUri && voyageUri !== 'TBN' && portCallData?.voyageUri !== voyageUri) {
      this.loadPortCalls(voyageUri, target);
    }
  }

  private tryAutoSelectConnectionPort(booking: Partial<BookingModel>): void {
    if (
      !booking?.voyage ||
      !booking.secondVoyage ||
      booking.secondVoyage === 'TBN' ||
      !booking.originPort ||
      !booking.destinationPort
    ) {
      return;
    }

    const primaryData = this.primaryPortCallData();
    const secondaryData = this.secondaryPortCallData();

    if (
      !this.hasPortCallsForVoyage(booking.voyage, primaryData) ||
      !this.hasPortCallsForVoyage(booking.secondVoyage, secondaryData)
    ) {
      return;
    }

    const primaryVoyage = this.findVoyageByUri(booking.voyage);
    const secondaryVoyage = this.findVoyageByUri(booking.secondVoyage);
    const sameVessel =
      !!primaryVoyage?.vessel &&
      !!secondaryVoyage?.vessel &&
      primaryVoyage.vessel === secondaryVoyage.vessel;
    const connectionPort = resolveSharedConnectionPort(
      primaryData.portCalls,
      secondaryData.portCalls,
      booking.originPort,
      booking.destinationPort,
      sameVessel
    );

    if (connectionPort && !booking.transshipmentPort) {
      this.syncBookingField(
        'transshipmentPort',
        booking as BookingModel,
        connectionPort
      );
    }
  }

  private findVoyageByUri(voyageUri: string): VoyageModel | undefined {
    const voyageId = voyageUri.split('/').pop();

    return this.dataStoreService
      .selectAll<VoyageModel>('voyages')()
      .find(voyage => String(voyage.id) === voyageId || voyage.id === voyageUri);
  }

  private hasPortCallsForVoyage(
    voyageUri: string | null | undefined,
    portCallData: VoyagePortCallData | null
  ): portCallData is VoyagePortCallData {
    return !!voyageUri && voyageUri !== 'TBN' && !!portCallData && portCallData.voyageUri === voyageUri;
  }

  /**
   * Returns `true` while the port calls for a voyage are being fetched from the API.
   * Used to disable port-selection dropdowns until the filtered options are ready.
   *
   * @param voyageUri - The voyage IRI to check.
   * @param loadingVoyages - The current in-flight voyage requests from `DataStoreService`.
   */
  private isPortCallsLoading(
    voyageUri: string | null | undefined,
    loadingVoyages: readonly LoadingVoyageState[]
  ): boolean {
    return (
      !!voyageUri && voyageUri !== 'TBN' &&
      loadingVoyages.some(
        (loadingVoyage: LoadingVoyageState) => loadingVoyage.id === voyageUri
      )
    );
  }

  private getCachedPortCallData(
    voyageUri: string
  ): VoyagePortCallData | undefined {
    return this.dataStoreService.getById<VoyagePortCallData>(
      this.portCallCacheStoreKey,
      voyageUri,
      portCallData => portCallData.voyageUri
    );
  }

  private markVoyageAsLoading(voyageUri: string): void {
    this.dataStoreService.upsertOne<LoadingVoyageState>(
      this.portCallLoadingStoreKey,
      { id: voyageUri },
      loadingVoyage => loadingVoyage.id
    );
  }

  private clearLoadingVoyage(voyageUri: string): void {
    this.dataStoreService.removeOne<LoadingVoyageState>(
      this.portCallLoadingStoreKey,
      voyageUri,
      loadingVoyage => loadingVoyage.id
    );
  }

  private clearLoadingVoyages(): void {
    this.dataStoreService.clear<LoadingVoyageState>(
      this.portCallLoadingStoreKey,
      loadingVoyage => loadingVoyage.id
    );
  }

  private getVoyagePortOptions(
    voyageUri: string | null | undefined,
    portCallData: VoyagePortCallData | null,
    target: 'primary' | 'secondary'
  ): SelectOption[] {
    this.ensurePortCallsLoaded(voyageUri, portCallData, target);

    if (!this.hasPortCallsForVoyage(voyageUri, portCallData)) {
      return this.allPortOptions();
    }

    return filterVoyagePortOptions(this.allPortOptions(), portCallData.portCalls);
  }

  private getLegPortOptions(
    voyageUri: string | null | undefined,
    portCallData: VoyagePortCallData | null,
    target: 'primary' | 'secondary',
    hubPort: string | null | undefined,
    beforeHub: boolean
  ): SelectOption[] {
    this.ensurePortCallsLoaded(voyageUri, portCallData, target);

    if (!this.hasPortCallsForVoyage(voyageUri, portCallData)) {
      return this.allPortOptions();
    }

    return filterLegPortOptions(
      this.allPortOptions(),
      portCallData.portCalls,
      hubPort,
      beforeHub
    );
  }

  /**
   * Fetches port calls for a voyage and stores them in the appropriate signal.
   *
   * Results are cached in `DataStoreService` by voyage URI to prevent duplicate
   * requests across form interactions. In-flight requests are also tracked in
   * `DataStoreService` so the UI can disable dependent dropdowns while loading. The subscription is
   * automatically cleaned up on component destroy via `takeUntilDestroyed`.
   *
   * @param voyageUri - The IRI of the voyage to fetch port calls for.
   * @param target - Whether to populate `primaryPortCallData` or `secondaryPortCallData`.
   */
  private loadPortCalls(voyageUri: string, target: 'primary' | 'secondary'): void {
    if (
      !voyageUri ||
      this.isPortCallsLoading(voyageUri, this.loadingVoyages())
    ) {
      return;
    }

    const cached = this.getCachedPortCallData(voyageUri);
    if (cached) {
      if (target === 'primary') {
        this.primaryPortCallData.set(cached);
      } else {
        this.secondaryPortCallData.set(cached);
      }
      this.refreshPortFieldOptions();
      return;
    }

    this.markVoyageAsLoading(voyageUri);
    const voyageId = voyageUri.split('/').pop() ?? '';
    this.voyageService
      .fetchPortCallsByVoyageId(voyageId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (portCalls) => {
          const cachedPortCallData = {
            voyageUri,
            portCalls: sortPortCallsBySequence(portCalls),
          };

          this.dataStoreService.upsertOne<VoyagePortCallData>(
            this.portCallCacheStoreKey,
            cachedPortCallData,
            portCallData => portCallData.voyageUri
          );

          if (target === 'primary') {
            this.primaryPortCallData.set(cachedPortCallData);
          } else {
            this.secondaryPortCallData.set(cachedPortCallData);
          }

          this.refreshPortFieldOptions();
          this.tryAutoSelectConnectionPort(
            this.bookingModalForm()?.value as BookingModel
          );
          this.clearLoadingVoyage(voyageUri);
        },
        error: () => {
          this.clearLoadingVoyage(voyageUri);
        },
      });
  }

  /**
   * Normalizes transshipment fields before the booking is sent to the API.
   *
   * Clears `secondVoyage` and `transshipmentPort` when transshipment is not required,
   * using `undefined` on CREATE (omit from payload) or `null` on EDIT (explicit clear).
   */
  onBeforeSave(payload: { data: BookingModel; mode: ModalMode }): void {
    if (!payload?.data) {
      return;
    }

    normalizeBookingTransshipmentPayload(payload.data, payload.mode);
  }

  clientName(clientId: string): string {
    const client = this.dataStoreService.getById<ClientModel>(
      'clients',
      clientId
    );
    return client ? clientNameTemplate(client) : '';
  }

  clientShortName(clientId: string): string {
    const client = this.dataStoreService.getById<ClientModel>(
      'clients',
      clientId
    );
    return client?.commercialName ?? '';
  }

  voyageName(voyage?: VoyageModel): string {
    return voyageNameTemplate(voyage);
  }

  formatLeg(voyage?: VoyageModel): string {
    if (!voyage) return 'TBN';
    const vessel = voyage.vesselDetails?.vesselName;
    const ref = voyage.getReference();
    if (vessel && ref) return `${vessel} - ${ref}`;
    return vessel || ref || 'TBN';
  }

  portName(portId: string): string {
    const port = this.dataStoreService.getById<PortModel>('ports', portId);
    return port ? portNameTemplate(port) : '';
  }

  currencyName(currencyId: string): string {
    return (
      this.dataStoreService
        .getById<VoyageModel>('currencies', currencyId)
        ?.getReference() ?? ''
    );
  }

  vesselNameFromVoyage(voyage?: VoyageModel): string {
    return voyage?.vesselDetails?.vesselName ?? '';
  }

  vesselName(vesselId: string): string {
    const vessel = this.dataStoreService.getById<VesselModel>('vessels', vesselId);
    return vessel?.vesselName ?? '';
  }

  private voyageSelectLabel(voyage: VoyageModel): string {
    return `${voyage.getReference()} - ${this.vesselName(voyage.vessel ?? 'N/A')}`;
  }

  /**
   * Extracts a user-facing error message from a booking API error response.
   * Passed to the generic table component as its `apiErrorHandler` callback.
   *
   * @param error - The raw HTTP error from Angular's `HttpClient`.
   * @param defaultMessage - Fallback message when no API detail is available.
   */
  onApiError = (error: any, defaultMessage: string): string => {
    return extractBookingApiErrorMessage(error, defaultMessage);
  };

  private hydratePorts(ports: PortModel[]): void {
    this.dataStoreService.hydrate<PortModel>(
      'ports',
      ports.map((port: PortModel) => {
        port.id = portUriTemplate(port.id);
        return port;
      })
    );
    this.refreshPortFieldOptions();
  }

  private refreshPortFieldOptions(): void {
    this.portsFieldVersion.update(version => version + 1);
  }

  private exportAllBookings(): void {
    this.confirmService
      .openConfirm({
        title: 'bookings.exportDialogConfirm',
      })
      .subscribe((result) => {
        if (result) {
          this.loaderService.showLoader();
          this.bookingService.exportBookings(this.activeFilters()).subscribe({
            next: (blob: any) => {
              console.log(blob);
              this.loaderService.hideLoader();
            },
            error: (error: HttpErrorResponse) => {
              this.loaderService.hideLoader();
              this.alertService.show({
                type: AlertType.ERROR,
                message: error.error?.detail ?? `errors.generic`,
                duration: 2000,
                inBottom: true,
              });
            },
          });
        }
      });
  }

  openImportModal(): void {
    this.showImportModal.set(true);
  }

  onImportModalClosed(): void {
    this.showImportModal.set(false);
  }

  onBookingsImported(): void {
    this.table?.onManualRefresh();
  }
}
